import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Formik } from "formik";
import * as Yup from "yup";
import ContentCard from "../../../components/misc/ContentCard";
import TextInput from "../../../components/misc/TextInput";
import CustomButton from "../../../components/CustomButton";
import Dropdown, { DropdownValue } from "../../../components/misc/Dropdown";
import {
  checkEndpointStatus,
  endpointStatus,
} from "../../../helpers/apiHelper";
import {
  getCutiePicking,
  editCutiePicking,
  createCutiePicking,
  getPickingBoxTypes,
  getPickingBoxTmsServices,
} from "../../../redux/api/inventory.api";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { clearPickingBoxes } from "../../../redux/reducer/inventory.reducer";
import {
  getPickingBoxTypeOptions,
  getPickingBoxTmsOptions,
} from "./tableConfigs/pickingBoxesTableConfig";
import { PickingBoxResponseInterface } from "../../../redux/interfaces/inventory.interface";

interface PickingBoxFormValues {
  nume: string;
  dimensiuni: string;
  greutate: number | string;
  stock: number | string;
  tip: number | string;
  tip_tms: number | string;
}

const initialValues: PickingBoxFormValues = {
  nume: "",
  dimensiuni: "",
  greutate: "",
  stock: "",
  tip: "",
  tip_tms: "",
};

const pickingBoxSchema = Yup.object({
  nume: Yup.string().trim().required("Campul este obligatoriu"),
  dimensiuni: Yup.string().trim().required("Campul este obligatoriu"),
  greutate: Yup.number()
    .typeError("Introdu o valoare numerica valida")
    .required("Campul este obligatoriu"),
  stock: Yup.number()
    .typeError("Introdu o valoare numerica valida")
    .required("Campul este obligatoriu"),
  tip: Yup.number().required("Campul este obligatoriu"),
  tip_tms: Yup.number().required("Campul este obligatoriu"),
});

export default function CreateOrUpdatePickingBox() {
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { edit, types, tmsServices } = useAppSelector(
    (state) => state.inventory.pickingBoxes,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [formikValues, setFormikValues] =
    useState<PickingBoxFormValues>(initialValues);
  const [selectedType, setSelectedType] = useState<DropdownValue | null>(null);
  const [selectedTmsType, setSelectedTmsType] = useState<DropdownValue | null>(
    null,
  );

  const typeOptions = useMemo(
    () => getPickingBoxTypeOptions(types.data),
    [types.data],
  );
  const tmsOptions = useMemo(
    () => getPickingBoxTmsOptions(tmsServices.data),
    [tmsServices.data],
  );
  const boxData = (edit.data as PickingBoxResponseInterface | null)?.data;
  const boxName = boxData?.nume ?? "";

  useEffect(() => {
    dispatch(getPickingBoxTypes());
    dispatch(getPickingBoxTmsServices());
    if (id) dispatch(getCutiePicking(id));
    else setFormikValues(initialValues);
  }, [dispatch, id]);

  useEffect(() => {
    if (!boxData || !id) return;
    setFormikValues({
      nume: boxData.nume ?? "",
      dimensiuni: boxData.dimensiuni ?? "",
      greutate: boxData.greutate ?? "",
      stock: boxData.stock ?? "",
      tip: boxData.tip ?? "",
      tip_tms: boxData.tip_tms ?? "",
    });
  }, [boxData, id]);

  useEffect(() => {
    setSelectedType(
      typeOptions.find(
        (option) => String(option.id) === String(formikValues.tip),
      ) ?? null,
    );
  }, [formikValues.tip, typeOptions]);

  useEffect(() => {
    setSelectedTmsType(
      tmsOptions.find(
        (option) => String(option.id) === String(formikValues.tip_tms),
      ) ?? null,
    );
  }, [formikValues.tip_tms, tmsOptions]);

  useEffect(() => {
    return () => {
      dispatch(clearPickingBoxes());
    };
  }, [dispatch]);

  const handleGoBack = () => navigate(-1);

  const handleSave = async (values: PickingBoxFormValues) => {
    const payload = {
      nume: values.nume.trim(),
      dimensiuni: values.dimensiuni.trim(),
      greutate: Number(values.greutate),
      stock: Number(values.stock),
      tip: Number(values.tip),
      tip_tms: Number(values.tip_tms),
    };
    setIsLoading(true);
    const resultAction = id
      ? await dispatch(editCutiePicking({ id, values: payload }))
      : await dispatch(createCutiePicking(payload));
    if (
      checkEndpointStatus(
        resultAction,
        id ? editCutiePicking : createCutiePicking,
      ) !== endpointStatus.pending
    ) {
      setIsLoading(false);
    }
    if (
      checkEndpointStatus(
        resultAction,
        id ? editCutiePicking : createCutiePicking,
      ) === endpointStatus.fulfilled
    ) {
      handleGoBack();
    }
  };

  return (
    <ContentCard
      cardTitle={
        id
          ? `Editare caracteristici ambalaj${boxName ? ` “${boxName}”` : ""}`
          : "Adauga cutie picking"
      }
      isLoading={!!id && edit.status === "loading"}>
      <Formik
        enableReinitialize
        initialValues={formikValues}
        validationSchema={pickingBoxSchema}
        onSubmit={handleSave}>
        {({
          isValid,
          errors,
          touched,
          getFieldProps,
          handleSubmit,
          isSubmitting,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit} noValidate id="picking-box-form">
            <div className="flex">
              <TextInput
                className="mb-4 mr-2"
                label="Nume"
                name="nume"
                error={errors.nume}
                touched={touched.nume}
                inputProps={getFieldProps("nume")}
              />
              <TextInput
                className="mb-4 mr-2"
                label="Dimensiuni"
                name="dimensiuni"
                error={errors.dimensiuni}
                touched={touched.dimensiuni}
                inputProps={getFieldProps("dimensiuni")}
              />
            </div>
            <div className="flex">
              <TextInput
                className="mb-4 mr-2"
                label="Greutate"
                name="greutate"
                type="number"
                error={errors.greutate}
                touched={touched.greutate}
                inputProps={{
                  ...getFieldProps("greutate"),
                  inputProps: { step: "any" },
                }}
              />
              <TextInput
                className="mb-4 mr-2"
                label="Stoc"
                name="stock"
                type="number"
                error={errors.stock}
                touched={touched.stock}
                inputProps={{
                  ...getFieldProps("stock"),
                  inputProps: { step: 1, min: 0 },
                }}
                onlyPositiveNumbers
              />
            </div>
            <div className="flex">
              <Dropdown
                className="mb-4 mr-2"
                title="Material ambalaj"
                name="tip"
                data={typeOptions}
                singleValue={selectedType}
                setSingleValue={(value) => {
                  setSelectedType(value);
                  setFieldValue("tip", value?.id ?? "");
                }}
                isLoading={types.status === "loading"}
                error={errors.tip}
                touched={touched.tip}
              />
              <Dropdown
                className="mb-4 mr-2"
                title="Tip TMS"
                name="tip_tms"
                data={tmsOptions}
                singleValue={selectedTmsType}
                setSingleValue={(value) => {
                  setSelectedTmsType(value);
                  setFieldValue("tip_tms", value?.id ?? "");
                }}
                isLoading={tmsServices.status === "loading"}
                error={errors.tip_tms}
                touched={touched.tip_tms}
              />
            </div>
            <div className="flex justify-between">
              <CustomButton variant="outlined" onClick={handleGoBack}>
                Anuleaza
              </CustomButton>
              <CustomButton
                type="submit"
                variant="contained"
                loading={isSubmitting || isLoading}
                disabled={!isValid || isSubmitting || isLoading}>
                Salveaza
              </CustomButton>
            </div>
          </form>
        )}
      </Formik>
    </ContentCard>
  );
}
